import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, timer } from 'rxjs';
import { map, switchMap, take, takeWhile } from 'rxjs/operators';
import { generateJWTToken } from 'src/app/core/helpers/jwt-token.utils';
import { IExportJob, IExportJobsResponse } from '../review-management.interfaces';

@Injectable({
  providedIn: 'root',
})
export class DownloadJobsService {
  private readonly exportJobsEndpoint = 'https://io.renderseo.com/reviews/all/export/jobs/';
  private readonly refreshJobsSubject = new Subject<void>();
  private readonly exportStartedSubject = new Subject<void>();
  /** Delay between successive export-job status polls. */
  private readonly pollIntervalMs = 5000;
  /** Safety cap on poll cycles so a stuck job cannot poll the endpoint forever. */
  private readonly maxPollAttempts = 60;
  /**
   * Minimum poll cycles that always run before polling may settle.
   * Bridges the gap where a just-created export job is not yet returned by the endpoint,
   * so the immediate first poll cannot end polling before the new job appears.
   */
  private readonly minPollAttempts = 2;
  /** Job statuses that indicate work is still in progress and polling should continue. */
  private readonly inProgressJobStatuses = new Set([
    'pending',
    'processing',
    'in_progress',
    'in-progress',
    'running',
    'queued',
    'started',
    'created',
    'new',
    'working',
    'submitted',
  ]);

  constructor(private http: HttpClient) {}

  /**
   * Retrieves export jobs for the active user/account pair.
   * The request uses the same JWT payload contract as other review-management endpoints and returns job entries for the global header dropdown.
   * @param userId Current user id used to sign the export-jobs JWT payload.
   * @param accountId Current account id used to sign the export-jobs JWT payload.
   * @returns Observable<IExportJobsResponse> with latest export jobs.
   */
  public getExportJobs(userId: number, accountId: number): Observable<IExportJobsResponse> {
    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000,
    };
    const jwtToken = generateJWTToken(payloadObj, '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a');
    const options = {
      headers: new HttpHeaders({
        'content-type': 'application/json',
        Authorization: `${jwtToken}`,
      }),
    };

    return this.http.get<IExportJobsResponse>(this.exportJobsEndpoint, options);
  }

  /**
   * Polls the export-jobs endpoint every `pollIntervalMs` until no job is in progress.
   * It emits the latest jobs response on each poll (starting immediately) so callers can refresh
   * their list. The first `minPollAttempts` cycles always run so a just-created export job that is
   * not yet visible cannot end polling prematurely; afterwards polling completes once all jobs are
   * finished or `maxPollAttempts` is reached.
   * @param userId Current user id used to sign each export-jobs JWT payload.
   * @param accountId Current account id used to sign each export-jobs JWT payload.
   * @returns Observable<IExportJobsResponse> emitting on every poll and completing when jobs settle.
   */
  public pollExportJobsUntilComplete(userId: number, accountId: number): Observable<IExportJobsResponse> {
    return timer(0, this.pollIntervalMs).pipe(
      switchMap((pollIndex) =>
        this.getExportJobs(userId, accountId).pipe(map((response) => ({ pollIndex, response })))
      ),
      take(this.maxPollAttempts),
      takeWhile(({ pollIndex, response }) => pollIndex < this.minPollAttempts || this.hasInProgressJobs(response), true),
      map(({ response }) => response)
    );
  }

  /**
   * Determines whether any job in the response is still in progress.
   * @param response Latest export-jobs response from the endpoint.
   * @returns True when at least one job is still being processed.
   */
  private hasInProgressJobs(response: IExportJobsResponse): boolean {
    const jobs = response?.jobs ?? [];
    return jobs.some((job) => this.isJobInProgress(job));
  }

  /**
   * Classifies a single job as in progress based on its status.
   * Statuses outside the known in-progress set (including empty/terminal) are treated as finished.
   * @param job Export job entry returned by the jobs endpoint.
   * @returns True when the job status indicates ongoing work.
   */
  private isJobInProgress(job: IExportJob): boolean {
    const status = (job?.status ?? '').toLowerCase().trim();
    return this.inProgressJobStatuses.has(status);
  }

  /**
   * Emits a cross-component refresh signal so listeners can reload export jobs.
   * This is triggered after export-job creation responses to keep the global dropdown current.
   * @returns None.
   */
  public notifyJobsUpdated(): void {
    this.refreshJobsSubject.next();
  }

  /**
   * Emits a signal the instant an export is requested, before the request resolves.
   * Listeners use this to start the download-button loading animation immediately on click.
   * @returns None.
   */
  public notifyExportStarted(): void {
    this.exportStartedSubject.next();
  }

  /**
   * Exposes a read-only stream that emits when an export has just been requested.
   * @returns Observable<void> that emits on each export-start signal.
   */
  public get exportStarted$(): Observable<void> {
    return this.exportStartedSubject.asObservable();
  }

  /**
   * Exposes a read-only stream of refresh events for export-job consumers.
   * Components subscribe to this observable to react to export completion events.
   * @returns Observable<void> that emits when jobs should be re-fetched.
   */
  public get jobsRefresh$(): Observable<void> {
    return this.refreshJobsSubject.asObservable();
  }
}
